import { useEffect, useState } from "react";
import { Page, Skeleton } from "../components/ui.jsx";
import { LEVEL_LABEL, RANKS } from "../data/levels.js";

// Human names for the internal view keys, so the report reads like the app
// rather than like the router.
const VIEW_LABEL = {
  home: "🏠 Home",
  dashboard: "🪪 My Dashboard",
  search: "🔍 Search",
  onboard: "🚀 Onboarding",
  ops: "📜 SOPs & Playbooks",
  culture: "🧬 Culture Codes",
  tribe: "🧭 myTRiBE+ Journey",
  thread: "💬 Class chatter",
  calendar: "🗓️ myTRiBE Calendar",
  wall: "🙏 Prayer & Testimony Wall",
  launch: "🎒 Back to School",
  // The page is gone; the label stays so the views it collected still render.
  homecoming: "🏠 Homecoming",
  "ship-happens": "⚓ Ship Happens",
  docs: "📚 Resources",
  dreamteam: "🙌 Dream Team",
  worship: "🎵 CLLXTV Music",
  leadershipplus: "📈 LEADERSHiP+",
  meet: "🤝 Request a meeting",
  org: "🗂️ Org Chart",
  report: "🐞 Report an issue",
  admin: "🔑 Roles & access",
  analytics: "📊 Analytics",
};

const ago = (ts) => {
  if (!ts) return "—";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// Compact number for axis ticks, so a busy month does not widen the gutter.
const tick = (n) => (n >= 1000 ? Math.round(n / 100) / 10 + "k" : String(n));

function Stat({ value, label, note, glyph }) {
  return (
    <div className="an-stat">
      <div className="an-stathead">
        <span className="an-lab">{label}</span>
        <span className="an-glyph" aria-hidden="true">
          {glyph}
        </span>
      </div>
      <div className="an-num">{value}</div>
      {note && <div className="an-note">{note}</div>}
    </div>
  );
}

// A ranked row: position, name, value, and a proportion bar. Width is relative
// to the largest row, so the shape of usage is readable even when totals are
// small. The leading row is accented — one highlight per list, not fifteen.
function Bar({ rank, label, count, max, sub }) {
  const pct = max ? Math.max(2, Math.round((count / max) * 100)) : 0;
  return (
    <div className={"an-row" + (rank === 1 ? " lead" : "")}>
      <div className="an-rowhead">
        <span className="an-rank">{rank}</span>
        <span className="an-rowname">{label}</span>
        <span className="an-rowcount">{count.toLocaleString()}</span>
      </div>
      <div className="an-track">
        <div className="an-fill" style={{ width: pct + "%" }} />
      </div>
      {sub && <div className="an-rowsub">{sub}</div>}
    </div>
  );
}

// Daily views as an area chart. Same numbers the column chart showed; the
// filled shape just makes the run of days read as a trend rather than as a
// row of independent bars.
function Trend({ trend }) {
  const W = 720;
  const H = 210;
  const PAD = { l: 42, r: 10, t: 14, b: 26 };
  const max = Math.max(1, ...trend.map((d) => d.views));
  const n = trend.length;

  const x = (i) => PAD.l + (n <= 1 ? 0 : (i * (W - PAD.l - PAD.r)) / (n - 1));
  const y = (v) => PAD.t + (1 - v / max) * (H - PAD.t - PAD.b);

  const pts = trend.map((d, i) => [x(i), y(d.views)]);
  const line = pts.map(([px, py], i) => (i ? "L" : "M") + px + " " + py).join(" ");
  const area = `${line} L ${x(n - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`;

  // Four gridlines including the baseline, matching the tick labels.
  const ticks = [0, 1, 2, 3].map((i) => (max / 3) * i);
  const band = n > 1 ? (W - PAD.l - PAD.r) / (n - 1) : W;

  return (
    <svg
      className="an-chart"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Daily views over ${n} days, peak ${max}`}
    >
      <defs>
        <linearGradient id="anFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {ticks.map((v) => (
        <g key={v}>
          <line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
          <text className="axis" x={PAD.l - 10} y={y(v) + 4} textAnchor="end">
            {tick(Math.round(v))}
          </text>
        </g>
      ))}

      <path className="area" d={area} />
      <path className="line" d={line} />

      {/* Invisible per-day targets keep the exact count on hover. */}
      {trend.map((d, i) => (
        <g key={d.day}>
          <rect
            className="hit"
            x={x(i) - band / 2}
            y={PAD.t}
            width={band}
            height={H - PAD.t - PAD.b}
          >
            <title>{`${d.day}: ${d.views} views, ${d.people} people`}</title>
          </rect>
          <circle className="dot" cx={x(i)} cy={y(d.views)} r="4" />
        </g>
      ))}

      <text className="axis" x={PAD.l} y={H - 6}>
        {trend[0]?.day.slice(5)}
      </text>
      <text className="axis" x={W - PAD.r} y={H - 6} textAnchor="end">
        {trend[n - 1]?.day.slice(5)}
      </text>
    </svg>
  );
}

export default function Analytics({ back }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [span, setSpan] = useState(30);

  useEffect(() => {
    setData(null);
    fetch(`/api/analytics?days=${span}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load analytics.");
        setData(d);
      })
      .catch((e) => setError(String(e.message || e)));
  }, [span]);

  if (error) {
    return (
      <Page back={back} eyebrow="Insight" title="Usage." tone="an-dash">
        <div className="card locked">
          <div className="name">
            <span>🔒 {error}</span>
          </div>
        </div>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page back={back} eyebrow="Insight" title="Usage." tone="an-dash">
        <Skeleton />
      </Page>
    );
  }

  const { totals, trend, topPeople, topViews, levels } = data;
  const maxView = topViews[0]?.count || 0;
  const maxPerson = topPeople[0]?.count || 0;
  const quiet = totals.views === 0;

  return (
    <Page
      back={back}
      eyebrow="Insight"
      title="Usage."
      intro="Who is actually using the hub, and what they open when they do."
      tone="an-dash"
    >
      <div className="chips">
        {[7, 30, 90].map((n) => (
          <button
            key={n}
            className={"chip" + (span === n ? " on" : "")}
            onClick={() => setSpan(n)}
          >
            Last {n} days
          </button>
        ))}
      </div>

      {quiet && (
        <div className="empty-note">
          No activity recorded yet in this window. Tracking started when this page was
          built, so history before then does not exist — give it a few days.
        </div>
      )}

      <div className="an-stats">
        <Stat value={totals.active7d} label="Active this week" glyph="👤" />
        <Stat value={totals.active30d} label="Active this month" glyph="📅" />
        <Stat
          value={totals.views.toLocaleString()}
          label="Page views"
          note={`over ${data.span} days`}
          glyph="👁"
        />
        <Stat
          value={totals.perPerson}
          label="Views per person"
          note="engagement depth"
          glyph="📊"
        />
      </div>

      <section className="an-card">
        <h3>Daily activity</h3>
        <p>Each point is one day. Hover for the exact count.</p>
        <Trend trend={trend} />
      </section>

      <section className="an-card">
        <h3>Who's using it</h3>
        <p>
          Ranked by page views. <b>Days</b> is how many separate days they showed up —
          often the truer signal of a habit.
        </p>
        {topPeople.length === 0 ? (
          <div className="empty-note">Nobody recorded yet.</div>
        ) : (
          topPeople.map((p, i) => (
            <Bar
              key={p.pid}
              rank={i + 1}
              label={p.name}
              count={p.count}
              max={maxPerson}
              sub={`${LEVEL_LABEL[p.level] || p.level} · ${p.days} ${
                p.days === 1 ? "day" : "days"
              } active · last seen ${ago(p.last)}`}
            />
          ))
        )}
      </section>

      <section className="an-card">
        <h3>What they open</h3>
        <p>Page views across everyone, most used first.</p>
        {topViews.length === 0 ? (
          <div className="empty-note">No pages recorded yet.</div>
        ) : (
          topViews.map((v, i) => (
            <Bar
              key={v.view}
              rank={i + 1}
              label={VIEW_LABEL[v.view] || v.view}
              count={v.count}
              max={maxView}
            />
          ))
        )}
      </section>

      <section className="an-card">
        <h3>Usage by tier</h3>
        <p>Whether the hub is reaching members or only leadership.</p>
        {RANKS.filter((l) => levels[l]).map((l) => {
          const top = Math.max(...RANKS.map((r) => levels[r] || 0), 1);
          return (
            <div className="an-hbar" key={l}>
              <span className="an-hlab">{LEVEL_LABEL[l]}</span>
              <span className="an-htrack">
                <span
                  className="an-hfill"
                  style={{ width: Math.round((levels[l] / top) * 100) + "%" }}
                />
              </span>
              <span className="an-hval">{levels[l].toLocaleString()}</span>
            </div>
          );
        })}
      </section>

      <div className="privacy-note">
        Named page views, visible to Senior only. Nothing typed into the app is recorded
        here — no message contents, no prayer requests. Counts are kept for{" "}
        {data.retentionDays} days and then deleted.
      </div>
    </Page>
  );
}
